import { useState } from 'react';
import {
  Thermometer,
  AlertTriangle,
  Settings,
  RefreshCw,
  Package,
  Bell,
  CheckCircle,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  StorageLocationCard,
  TemperatureChart,
  AlertConfigModal,
} from '@/features/inventory/components';
import {
  useStorageLocations,
  useTemperatureHistory,
  useUpdateAlertConfig,
  useUpdateThresholds,
  useAcknowledgeAlert,
} from '@/features/inventory/hooks';
import type {
  StorageLocationWithTemp,
  AlertConfig,
  TemperatureThresholds,
} from '@/features/inventory/types/storage';
import { toast } from 'sonner';
import { DEFAULT_SITE_ID } from './inventory-constants';

export function StorageView() {
  const [selectedLocation, setSelectedLocation] = useState<StorageLocationWithTemp | null>(null);
  const [showAlertConfig, setShowAlertConfig] = useState(false);

  const {
    data: storageData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useStorageLocations(DEFAULT_SITE_ID);

  const {
    data: historyData,
    isLoading: isLoadingHistory,
  } = useTemperatureHistory(
    DEFAULT_SITE_ID,
    selectedLocation?.locationId ?? null
  );

  const updateAlertConfig = useUpdateAlertConfig(DEFAULT_SITE_ID);
  const updateThresholds = useUpdateThresholds(DEFAULT_SITE_ID);
  const acknowledgeAlert = useAcknowledgeAlert(DEFAULT_SITE_ID);

  const locations = storageData?.data ?? [];
  const criticalCount = locations.filter((l) => l.temperatureStatus === 'critical').length;
  const warningCount = locations.filter((l) => l.temperatureStatus === 'warning').length;
  const normalCount = locations.filter((l) => l.temperatureStatus === 'normal').length;

  const handleSaveAlertConfig = (config: AlertConfig) => {
    if (!selectedLocation) return;
    updateAlertConfig.mutate(
      {
        locationId: selectedLocation.locationId,
        config 
      },
      {
        onSuccess: () => {
          toast.success('Alert settings saved');
          setShowAlertConfig(false);
        },
        onError: () => toast.error('Failed to save alert settings'),
      }
    );
  };

  const handleSaveThresholds = (thresholds: TemperatureThresholds) => {
    if (!selectedLocation) return;
    updateThresholds.mutate(
      {
        locationId: selectedLocation.locationId,
        thresholds 
      },
      {
        onSuccess: () => toast.success('Thresholds updated'),
        onError: () => toast.error('Failed to update thresholds'),
      }
    );
  };

  const handleAcknowledgeAlert = (alertId: string) => {
    if (!selectedLocation) return;
    acknowledgeAlert.mutate(
      {
        locationId: selectedLocation.locationId,
        alertId 
      },
      {
        onSuccess: () => toast.success('Alert acknowledged'),
        onError: () => toast.error('Failed to acknowledge alert'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skeleton-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertTriangle className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load storage locations</p>
          <Button variant="outline" className="mt-4" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="grid gap-4 md:grid-cols-3">
          <SummaryCard
            title="Critical"
            count={criticalCount}
            icon={AlertTriangle}
            color="red"
          />
          <SummaryCard
            title="Warning"
            count={warningCount}
            icon={AlertTriangle}
            color="amber"
          />
          <SummaryCard
            title="Normal"
            count={normalCount}
            icon={CheckCircle}
            color="green"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <h3 className="font-medium text-gray-700">Storage Locations</h3>
          {locations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Package className="mb-4 h-10 w-10 text-muted-foreground" />
                <p className="text-muted-foreground">No storage locations configured</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {locations.map((location) => (
                <StorageLocationCard
                  key={location.locationId}
                  location={location}
                  isSelected={selectedLocation?.locationId === location.locationId}
                  onClick={() => setSelectedLocation(location)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          {selectedLocation ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-700">
                  {selectedLocation.name} - Temperature History
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAlertConfig(true)}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Alert Settings
                </Button>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Thermometer className="h-5 w-5" />
                      24-Hour Temperature Log
                    </CardTitle>
                    {selectedLocation.alertConfig.enabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700">
                        <Bell className="h-3 w-3" />
                        Alerts On
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingHistory ? (
                    <div className="h-[250px] animate-pulse rounded bg-muted" />
                  ) : (
                    <TemperatureChart
                      readings={historyData?.readings ?? []}
                      thresholds={selectedLocation.thresholds}
                      alerts={historyData?.alerts ?? []}
                      height={250}
                    />
                  )}
                </CardContent>
              </Card>

              {historyData?.alerts && historyData.alerts.filter((a) => !a.acknowledged).length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base text-amber-800">
                      <AlertTriangle className="h-5 w-5" />
                      Unacknowledged Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {historyData.alerts
                        .filter((a) => !a.acknowledged)
                        .map((alert) => (
                          <div
                            key={alert.id}
                            className="flex items-center justify-between rounded-md bg-white p-3 shadow-sm"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{alert.message}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(alert.timestamp).toLocaleString()}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAcknowledgeAlert(alert.id)}
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Acknowledge
                            </Button>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <AlertConfigModal
                isOpen={showAlertConfig}
                onClose={() => setShowAlertConfig(false)}
                location={selectedLocation}
                onSaveAlerts={handleSaveAlertConfig}
                onSaveThresholds={handleSaveThresholds}
                isSaving={updateAlertConfig.isPending || updateThresholds.isPending}
              />
            </>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Thermometer className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-medium text-gray-700">
                  Select a storage location
                </p>
                <p className="text-muted-foreground">
                  View temperature history and configure alerts
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  count,
  icon: iconComponent,
  color,
}: {
  readonly title: string;
  readonly count: number;
  readonly icon: typeof AlertTriangle;
  readonly color: 'red' | 'amber' | 'green';
}) {
  const SummaryIcon = iconComponent;
  const colorStyles = {
    red: 'text-red-500',
    amber: 'text-amber-500',
    green: 'text-green-500',
  };

  const countStyles = {
    red: 'text-red-600',
    amber: 'text-amber-600',
    green: 'text-green-600',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <SummaryIcon className={`h-5 w-5 ${colorStyles[color]}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${countStyles[color]}`}>{count}</div>
      </CardContent>
    </Card>
  );
}
